import React, { useState, useEffect, useRef } from "react";
import { get } from "lodash";
import Papa from "papaparse";
import {
  SdfBox,
  SdfCheckbox,
  SdfCheckboxGroup,
  SdfIcon,
  SdfInput,
  SdfSelect,
} from '@synerg/react-components';
import "./modifyScope.scss";

export const ScopeStep = (props = {}) => {
  const {
    isB2bScopes,
    categories,
    scopes,
    data = {},
    setData,
    preSelectedScopes,
  } = props;

  const [disableCategorySelection, setDisableCategorySelection] =
    useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchedText, setSearchedText] = useState("");
  const [viewSelectedScopes, setViewSelectedScopes] = useState(false);
  const [displayedScopes, setDisplayedSCopes] = useState([]);
  const [currentSelectedScope, setCurrentSelectedScope] = useState([]);
  const [selectedScopes, setSelectedScopes] = useState([]);

  // file upload
  const fileInputRef = useRef(null);
  const [csvValidation, setCsvValidation] = useState({
    success: false,
    error: false,
  });

  useEffect(() => {
    if (
      isB2bScopes &&
      data &&
      data.b2bApplicationScopes &&
      Array.isArray(data.b2bApplicationScopes) &&
      data.b2bApplicationScopes.length > 0
    ) {
      setCurrentSelectedScope(data.b2bApplicationScopes);
      setSelectedScopes(data.b2bApplicationScopes);
    } else if (
      isB2bScopes === false &&
      data &&
      data.b2cApplicationScopes &&
      Array.isArray(data.b2cApplicationScopes) &&
      data.b2cApplicationScopes.length > 0
    ) {
      setCurrentSelectedScope(data.b2cApplicationScopes);
      setSelectedScopes(data.b2bApplicationScopes);
    } else if (preSelectedScopes.length > 0) {
      setSelectedScopes(preSelectedScopes);
      setCurrentSelectedScope([...preSelectedScopes]);
      if (isB2bScopes) {
        setData((prevState) => ({
          ...prevState,
          b2bApplicationScopes: preSelectedScopes,
        }));
      } else {
        setData((prevState) => ({
          ...prevState,
          b2cApplicationScopes: preSelectedScopes,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedScopes]);

  useEffect(() => {
    setSelectedScopes(currentSelectedScope);
  }, [currentSelectedScope]);

  useEffect(() => {
    if (categories[0]) {
      setDisplayedSCopes(filterBasedOnCategory(categories[0]));
      setSelectedCategory({
        label: categories[0].toUpperCase(),
        value: categories[0],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, scopes]);

  const useDebounce = (value, delay) => {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(
      () => {
        // Update debounced value after delay
        const handler = setTimeout(() => {
          setDebouncedValue(value);
        }, delay);
        // Cancel the timeout if value changes (also on delay change or unmount)
        // This is how we prevent debounced value from updating if value is changed ...
        // .. within the delay period. Timeout gets cleared and restarted.
        return () => {
          clearTimeout(handler);
        };
      },
      [value, delay] // Only re-call effect if value or delay changes
    );
    return debouncedValue;
  };

  const debouncedSearchTerm = useDebounce(searchedText, 500);

  useEffect(() => {
    let filteredScopes = scopes;

    if (viewSelectedScopes) {
      setDisableCategorySelection(true);
    } else {
      setDisableCategorySelection(false);
    }

    if (viewSelectedScopes && searchedText) {
      filteredScopes = selectedScopes.filter((s) => s.includes(searchedText));
    }
    if (viewSelectedScopes && !searchedText) {
      filteredScopes = selectedScopes;
    }
    if (!viewSelectedScopes && searchedText) {
      filteredScopes = filterBasedOnCategory(selectedCategory, true).filter(
        (s) => s.includes(searchedText)
      );
    }
    if (!viewSelectedScopes && !searchedText) {
      filteredScopes = filterBasedOnCategory(selectedCategory, true);
    }

    setDisplayedSCopes(filteredScopes);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, viewSelectedScopes, debouncedSearchTerm]);

  const filterBasedOnCategory = (cat, exactPathMatch = false) => {
    const category = lowerCase(cat.value);
    if (category === "other") {
      return scopes.filter((s) => s.indexOf("/") !== 0);
    } else if (exactPathMatch) {
      const regex = new RegExp(`^/${category}/.*`, "i");
      return scopes.filter((s) => regex.test(lowerCase(s)));
    } else {
      return scopes.filter((s) => lowerCase(s).includes(category.trim()));
    }
  };

  const lowerCase = (text = "") => {
    return text && text.toLowerCase();
  };

  const onScopesChange = (selectedItems) => {
    if (isB2bScopes) {
      setData((prevState) => ({
        ...prevState,
        b2bApplicationScopes: selectedItems,
      }));
    } else {
      setData((prevState) => ({
        ...prevState,
        b2cApplicationScopes: selectedItems,
      }));
    }
  };

  const validateCSV = (file) => {
    if (!file) {
      return { valid: false, message: "Error occurred, please try again." };
    }

    const validTypes = ["text/csv", "application/vnd.ms-excel"];

    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        message: "Uploaded file is not valid. Valid file types: CSV",
      };
    }

    const uploadSize = file.size ? file.size : 0;
    const sizeInMb = uploadSize / 1048576;

    if (uploadSize === 0 || !file.size) {
      return { valid: false, message: "Uploaded file is empty." };
    }

    if (sizeInMb > 20) {
      return { valid: false, message: "Uploaded file is too large." };
    }

    return { valid: true, code: "valid" };
  };

  const onFileChange = async (event) => {
    const uploadCsv = get(event, "target.files[0]");
    const fileStatus = validateCSV(uploadCsv);

    if (!fileStatus.valid) {
      fileInputRef.current.value = "";
      setCsvValidation({
        error: true,
        success: false,
        errorMsg: fileStatus.message,
      });
      return;
    }

    try {
      Papa.parse(uploadCsv, {
        encoding: "UTF-8",
        worker: true,
        skipEmptyLines: true,
        complete: (results) => {
          const rows = results.data;

          // loop over each row and find each scope in domain. If found, add it to selected sceopes.
          // Otherwise, throw error and report row index.
          const scopesNotFound = [];
          const scopesToAdd = [];
          if (rows && rows.length > 0) {
            rows.forEach((scopeRow, index) => {
              const found = scopes.find((scopeStr, scopesIndex) => {
                if (
                  scopeStr &&
                  scopeRow &&
                  scopeRow.length > 0 &&
                  scopeStr.trim() === scopeRow[0].trim()
                ) {
                  return true;
                } else {
                  return false;
                }
              });
              if (!found) {
                const hasScopeRow = scopeRow && scopeRow.length > 0;
                scopesNotFound.push({
                  data: hasScopeRow ? scopeRow[0] : scopeRow,
                  indexNotFoundAt: index + 1,
                });
              } else {
                if (
                  !selectedScopes.find(
                    (element) => element.trim() === found.trim()
                  )
                ) {
                  scopesToAdd.push(found);
                }
              }
            });

            if (scopesToAdd.length > 0) {
              onScopesChange([...scopesToAdd, ...selectedScopes]);
            }

            if (scopesNotFound.length === 0) {
              setCsvValidation({ error: false, success: true });
            } else {
              const notFoundIndexes = scopesNotFound.map(
                (item) => item.indexNotFoundAt
              );
              setCsvValidation({
                error: true,
                success: false,
                errorMsg: `An error occurred on the following line(s): ${notFoundIndexes.join(
                  ", "
                )}. Please try again.`,
              });
            }
          } else {
            // empty data in csv
            setCsvValidation({
              error: true,
              success: false,
              errorMsg: `CSV file is empty.`,
            });
          }
        },
        error: (error) => {},
      });
    } catch (e) {
      setCsvValidation({
        error: true,
        success: false,
        errorMsg: "Error occurred, please try again.",
      });
    }
    fileInputRef.current.value = "";
  };

  return (
    <div className="w-full">
      <div className="flex">
        <div className="w-3/4">
          <div className="text-heading-05 font-bold">
            Add {isB2bScopes ? "B2B" : "B2C"} APIs and Event Notifications
          </div>
          <div className="text-body-01">
            Bulk upload canonicals as a CSV file, or select paths to add and
            unselect to remove individiaully.
          </div>
        </div>
        <div className="w-1/4">
          <div className="file-upload-container">
            <label htmlFor="scopecsv" className="file-upload-button">
              <SdfIcon icon="action-upload"></SdfIcon>
              <span className="text">Bulk upload scope</span>
              <input
                type="file"
                ref={fileInputRef}
                onChange={onFileChange}
                title=""
                className="file-input"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                id="scopecsv"
              />
            </label>
          </div>
          <div className="text-body-01 color-grey">or add manually below</div>
          {csvValidation.success}
        </div>
      </div>
      <div className="flex mt-6 gap-6">
        <div className="w-1/4">
          <SdfSelect
            label="HCM Domain"
            placeholder="Select HCM Domain"
            className="w-full"
            items={categories.map((item) => ({
              label: item.toUpperCase(),
              value: item,
            }))}
            onSdfChange={(event) => setSelectedCategory(event.detail)}
            value={selectedCategory}
            disabled={disableCategorySelection ? true : undefined}
          ></SdfSelect>
        </div>
        <div className="w-3/4">
          <SdfInput
            label="Search"
            placeholder="Search..."
            className="w-full"
            onSdfInput={(event) => setSearchedText(event.detail)}
            value={searchedText}
          ></SdfInput>
        </div>
      </div>
      {(selectedScopes && selectedScopes.length >= 0) && <div className="flex mt-8 gap-6">
        <div className="w-2/4 text-heading-04 font-bold">
          Selected scopes ({selectedScopes.length})
        </div>
        <div className="w-2/4 text-right">
          <SdfCheckbox
            label="View selected scopes only"
            onSdfChange={(e) => setViewSelectedScopes(e.detail)}
            value={viewSelectedScopes}
          />
        </div>
      </div>}
      <div className="w-full">
        <SdfBox variant="filled" className="max-h-md overflow-auto">
          <SdfCheckboxGroup
            hideLabel={true}
            onSdfChange={(e) => {
              onScopesChange(e.detail);
              setSelectedScopes(e.detail);
            }}
            value={selectedScopes}
          >
            {selectedScopes && displayedScopes.map((item, key) => (
              <SdfCheckbox
                className="mb-2"
                key={key}
                label={item}
                value={item}
                checked={selectedScopes.indexOf(item) !== -1 ? true : false}
              />
            ))}
          </SdfCheckboxGroup>
        </SdfBox>
      </div>
    </div>
  );
};
